import React, { useState, useEffect } from 'react';
import { Shield, Power, AlertCircle, Check, Lock, Eye, EyeOff } from 'lucide-react';

const AdminDashboard = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [saleActive, setSaleActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [adminPassword, setAdminPassword] = useState('');

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    // Check if already logged in (session storage)
    const savedPassword = sessionStorage.getItem('adminPassword');
    if (savedPassword) {
      setAdminPassword(savedPassword);
      setIsLoggedIn(true);
      fetchSaleStatus();
    }
  }, []);

  const fetchSaleStatus = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/sale-status`);
      if (response.ok) {
        const data = await response.json();
        setSaleActive(data.is_active);
      }
    } catch (err) {
      console.error('Error fetching sale status:', err);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password })
      });

      if (response.ok) {
        setIsLoggedIn(true);
        setAdminPassword(password);
        sessionStorage.setItem('adminPassword', password);
        fetchSaleStatus();
      } else {
        setError('Invalid password');
      }
    } catch (err) {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleSale = async () => {
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/admin/toggle-sale`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          is_active: !saleActive,
          admin_password: adminPassword
        })
      });

      if (response.ok) {
        const data = await response.json();
        setSaleActive(data.is_active);
        setSuccess(data.message);
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError('Failed to toggle sale. Please try again.');
      }
    } catch (err) {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    setAdminPassword('');
    setPassword('');
    sessionStorage.removeItem('adminPassword');
  };

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="bg-gray-900 rounded-2xl p-8 border border-gray-800">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-blue-400" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">Admin Dashboard</h1>
              <p className="text-gray-400">Enter your admin password to continue</p>
            </div>

            <form onSubmit={handleLogin}>
              <div className="mb-6">
                <label className="block text-sm text-gray-400 mb-2">Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-blue-500 pr-12"
                    placeholder="Enter admin password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              {error && (
                <div className="mb-4 p-3 bg-red-500/20 border border-red-500/30 rounded-lg flex items-center gap-2 text-red-400">
                  <AlertCircle size={18} />
                  <span>{error}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-blue-500 hover:bg-blue-600 text-white font-semibold py-3 rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    <Lock size={18} />
                    Login
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black p-4 md:p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
              <Shield className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">Admin Dashboard</h1>
              <p className="text-sm text-gray-400">Pro-Wash Mobile Detailing</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="text-gray-400 hover:text-white text-sm"
          >
            Logout
          </button>
        </div>

        {/* Success/Error Messages */}
        {success && (
          <div className="mb-6 p-4 bg-green-500/20 border border-green-500/30 rounded-lg flex items-center gap-2 text-green-400">
            <Check size={18} />
            <span>{success}</span>
          </div>
        )}
        {error && (
          <div className="mb-6 p-4 bg-red-500/20 border border-red-500/30 rounded-lg flex items-center gap-2 text-red-400">
            <AlertCircle size={18} />
            <span>{error}</span>
          </div>
        )}

        {/* Sale Toggle Card */}
        <div className="bg-gray-900 rounded-2xl p-6 md:p-8 border border-gray-800">
          <div className="flex items-start justify-between mb-6">
            <div>
              <h2 className="text-xl font-bold text-white mb-2">Site-Wide Sale</h2>
              <p className="text-gray-400">Toggle 20% off across all services</p>
            </div>
            <div className={`px-3 py-1 rounded-full text-sm font-medium ${
              saleActive 
                ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
                : 'bg-gray-700/50 text-gray-400 border border-gray-600'
            }`}>
              {saleActive ? 'ACTIVE' : 'INACTIVE'}
            </div>
          </div>

          {/* Toggle Switch */}
          <div className="flex items-center justify-between p-6 bg-gray-800/50 rounded-xl mb-6">
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                saleActive ? 'bg-green-500/20' : 'bg-gray-700'
              }`}>
                <Power className={`w-6 h-6 ${saleActive ? 'text-green-400' : 'text-gray-400'}`} />
              </div>
              <div>
                <p className="text-white font-medium">20% Off Everything</p>
                <p className="text-sm text-gray-400">
                  {saleActive 
                    ? 'Sale prices are visible to all customers' 
                    : 'Regular prices are shown to customers'}
                </p>
              </div>
            </div>

            <button
              onClick={handleToggleSale}
              disabled={loading}
              className={`relative w-16 h-8 rounded-full transition-colors ${
                saleActive ? 'bg-green-500' : 'bg-gray-600'
              } ${loading ? 'opacity-50' : ''}`}
            >
              <div className={`absolute top-1 w-6 h-6 bg-white rounded-full transition-transform ${
                saleActive ? 'left-9' : 'left-1'
              }`} />
            </button>
          </div>

          {/* Preview */}
          <div className="border border-gray-700/50 rounded-xl p-4">
            <p className="text-sm text-gray-400 mb-3">Price Preview</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div className="bg-gray-800/50 rounded-lg p-3">
                <p className="text-xs text-gray-400 mb-1">Full Detail Package</p>
                {saleActive ? (
                  <div className="flex items-baseline gap-2">
                    <span className="text-lg font-bold text-green-400">$239</span>
                    <span className="text-sm text-gray-500 line-through">$299</span>
                  </div>
                ) : (
                  <span className="text-lg font-bold text-white">$299</span>
                )}
              </div>
              <div className="bg-gray-800/50 rounded-lg p-3">
                <p className="text-xs text-gray-400 mb-1">Interior Only</p>
                {saleActive ? (
                  <div className="flex items-baseline gap-2">
                    <span className="text-lg font-bold text-green-400">$159</span>
                    <span className="text-sm text-gray-500 line-through">$199</span>
                  </div>
                ) : (
                  <span className="text-lg font-bold text-white">$199</span>
                )}
              </div>
              <div className="bg-gray-800/50 rounded-lg p-3">
                <p className="text-xs text-gray-400 mb-1">5-Year Ceramic</p>
                {saleActive ? (
                  <div className="flex items-baseline gap-2">
                    <span className="text-lg font-bold text-green-400">$639</span>
                    <span className="text-sm text-gray-500 line-through">$799</span>
                  </div>
                ) : (
                  <span className="text-lg font-bold text-white">$799</span>
                )}
              </div>
            </div>
          </div>

          {/* Info */}
          <div className="mt-6 p-4 bg-blue-500/10 border border-blue-500/20 rounded-xl">
            <p className="text-sm text-blue-300">
              <strong>Note:</strong> When the sale is active, all prices on the website will show 20% off with the original price crossed out. A sale banner will also appear at the top of the page.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
